import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { rmAPI } from '../../services/api';

const OUTCOMES = [
  { value: 'connected', label: 'Connected' },
  { value: 'no_answer', label: 'No answer' },
  { value: 'callback_later', label: 'Callback later' },
  { value: 'message_sent', label: 'Message sent' },
  { value: 'waiting_for_availability', label: 'Waiting for availability' },
  { value: 'ptm_scheduled', label: 'PTM scheduled' },
];

const SENTIMENTS = [
  { value: 'positive', label: 'Positive' },
  { value: 'negative', label: 'Negative' },
  { value: 'neutral', label: 'Neutral' },
];

const STUDENT_TYPE_LABEL = {
  normal: 'Normal',
  autistic: 'Autistic',
  special_needs: 'Special needs',
};

function sentimentChipColor(value) {
  if (value === 'positive') return 'success';
  if (value === 'negative') return 'error';
  return 'default';
}

function Field({ label, value }) {
  return (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography>{value || '—'}</Typography>
    </Grid>
  );
}

function AssessmentView({ report }) {
  if (!report) return null;
  const modules = report.assessmentData?.modules || [];
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {report.reportMonth} · {report.teacherName || 'Teacher'}
      </Typography>
      {report.assessmentData?.overallRemarks || report.remarks ? (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
          {report.assessmentData?.overallRemarks || report.remarks}
        </Typography>
      ) : null}
      {modules.map((mod) => (
        <Box key={mod.moduleId || mod.moduleName} sx={{ mb: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            {mod.moduleName || mod.moduleCode || 'Module'}
          </Typography>
          {(mod.parameters || []).map((p) => (
            <Typography key={p.parameterId || p.name} variant="caption" display="block" color="text.secondary">
              {p.name || p.parameterName}: {p.selectedLabel || p.value || p.comment || '—'}
            </Typography>
          ))}
        </Box>
      ))}
    </Box>
  );
}

export default function RmStudentDetail() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState('connected');
  const [notes, setNotes] = useState('');
  const [sentiment, setSentiment] = useState('');
  const [spokenAt, setSpokenAt] = useState('');
  const [nextFollowUp, setNextFollowUp] = useState('');
  const [openReportId, setOpenReportId] = useState('');

  const detailQuery = useQuery(['rm-student', studentId], async () => {
    const res = await rmAPI.getStudent(studentId);
    return res.data?.data;
  });
  const feedbackQuery = useQuery(['rm-feedback', studentId], async () => {
    const res = await rmAPI.listFeedback(studentId);
    return res.data?.data || [];
  });
  const assessmentsQuery = useQuery(['rm-assessments', studentId], async () => {
    const res = await rmAPI.listAssessments(studentId);
    return res.data?.data || [];
  });
  const reportQuery = useQuery(
    ['rm-assessment', studentId, openReportId],
    async () => {
      const res = await rmAPI.getAssessment(studentId, openReportId);
      return res.data?.data;
    },
    { enabled: !!openReportId }
  );

  const addMut = useMutation((payload) => rmAPI.addFeedback(studentId, payload), {
    onSuccess: () => {
      toast.success('Parent call saved');
      setNotes('');
      setSentiment('');
      setNextFollowUp('');
      queryClient.invalidateQueries(['rm-feedback', studentId]);
      queryClient.invalidateQueries(['rm-students']);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Could not save call'),
  });

  const student = detailQuery.data;
  const title = useMemo(() => {
    if (!student) return 'RM student';
    return `${student.first_name || ''} ${student.last_name || ''}`.trim();
  }, [student]);

  if (detailQuery.isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (!student) {
    return (
      <Box>
        <Typography>Student not found</Typography>
        <Button onClick={() => navigate('/rm')}>Back</Button>
      </Box>
    );
  }

  return (
    <Box>
      <Button onClick={() => navigate('/rm')} sx={{ mb: 1 }}>
        Back to RM list
      </Button>
      <Typography variant="h4" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {student.display_id} · RM {student.rm_name || 'unassigned'} · closer {student.closer_name || '—'}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Student details
            </Typography>
            <Grid container spacing={1}>
              <Field label="Parent / father" value={student.father_name} />
              <Field label="Mother" value={student.mother_name} />
              <Field label="Phone" value={student.phone} />
              <Field label="Email" value={student.email} />
              <Field label="Country" value={student.country} />
              <Field label="State" value={student.state} />
              <Field label="City" value={student.city} />
              <Field label="Zip code" value={student.postal_code} />
              <Field label="Address" value={student.address} />
              <Field label="Timezone" value={student.timezone} />
              <Field label="Age group" value={student.age_group} />
              <Field label="Grade" value={student.class_grade} />
              <Field
                label="Student type"
                value={STUDENT_TYPE_LABEL[student.student_type] || student.student_type}
              />
              <Field label="Sales user" value={student.closer_name} />
              <Field
                label="Batch / teacher"
                value={
                  (student.assignments || [])
                    .map((a) => [a.batch_name, a.teacher_name].filter(Boolean).join(' · '))
                    .join('; ') || '—'
                }
              />
              <Field
                label="Batch start date"
                value={
                  (student.assignments || [])
                    .map((a) => [a.batch_name, a.batch_start_date].filter(Boolean).join(' · '))
                    .join('; ') || '—'
                }
              />
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Student notes
                </Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{student.notes || '—'}</Typography>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Approved assessments
            </Typography>
            {(assessmentsQuery.data || []).length === 0 ? (
              <Typography color="text.secondary">No approved reports yet.</Typography>
            ) : (
              <Stack spacing={1}>
                {(assessmentsQuery.data || []).map((row) => (
                  <Button
                    key={row.id}
                    variant={openReportId === row.id ? 'contained' : 'outlined'}
                    onClick={() => setOpenReportId(row.id === openReportId ? '' : row.id)}
                  >
                    {row.report_month} · {row.teacher_name || 'Teacher'}
                  </Button>
                ))}
              </Stack>
            )}
            {reportQuery.isLoading ? <CircularProgress size={20} sx={{ mt: 2 }} /> : null}
            {reportQuery.data ? (
              <Box mt={2}>
                <AssessmentView report={reportQuery.data} />
              </Box>
            ) : null}
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Log parent call
            </Typography>
            <Stack spacing={2}>
              <TextField
                select
                label="Outcome"
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
              >
                {OUTCOMES.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Spoken at"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={spokenAt}
                onChange={(e) => setSpokenAt(e.target.value)}
              />
              <TextField
                label="Notes"
                multiline
                minRows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
              <TextField
                select
                label="Sentiment"
                value={sentiment}
                onChange={(e) => setSentiment(e.target.value)}
              >
                <MenuItem value="">Not set</MenuItem>
                {SENTIMENTS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Next follow-up"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={nextFollowUp}
                onChange={(e) => setNextFollowUp(e.target.value)}
              />
              <Button
                variant="contained"
                disabled={!notes.trim() || addMut.isLoading}
                onClick={() =>
                  addMut.mutate({
                    outcome,
                    notes: notes.trim(),
                    sentiment: sentiment || null,
                    spoken_at: spokenAt ? new Date(spokenAt).toISOString() : undefined,
                    next_follow_up_date: nextFollowUp || null,
                  })
                }
              >
                Save call
              </Button>
            </Stack>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Call history
            </Typography>
            {(feedbackQuery.data || []).map((row) => (
              <Box key={row.id} sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip size="small" label={OUTCOMES.find((o) => o.value === row.outcome)?.label || row.outcome} />
                  {row.sentiment ? (
                    <Chip
                      size="small"
                      color={sentimentChipColor(row.sentiment)}
                      label={SENTIMENTS.find((s) => s.value === row.sentiment)?.label || row.sentiment}
                    />
                  ) : null}
                  <Typography variant="caption" color="text.secondary">
                    {row.spoken_at ? new Date(row.spoken_at).toLocaleString() : ''} · {row.created_by_name}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                  {row.notes}
                </Typography>
                {row.next_follow_up_date ? (
                  <Typography variant="caption" color="text.secondary">
                    Next follow-up {String(row.next_follow_up_date).slice(0, 10)}
                  </Typography>
                ) : null}
                <Divider sx={{ mt: 1 }} />
              </Box>
            ))}
            {(feedbackQuery.data || []).length === 0 ? (
              <Typography color="text.secondary">No calls logged yet.</Typography>
            ) : null}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
